/**
 * Promo Popup - Current Offers
 * Shows popup after 3.5 seconds, once per session
 */

class PromoPopup {
  constructor() {
    this.popup = document.getElementById('promo-popup');
    if (!this.popup) return;

    this.overlay = this.popup.querySelector('.promo-popup__overlay');
    this.closeButton = this.popup.querySelector('.promo-popup__close');
    this.container = this.popup.querySelector('.promo-popup__container');

    this.STORAGE_KEY = 'marea_offers_popup_dismissed';
    this.DELAY_MS = 3500; // 3.5 seconds

    this.previousActiveElement = null;
    this.focusableElements = null;

    this.init();
  }

  init() {
    // Check if popup was already dismissed in this session
    if (this.wasAlreadyDismissed()) {
      return;
    }

    // Set up event listeners
    this.bindEvents();

    // Show popup after delay
    setTimeout(() => this.show(), this.DELAY_MS);
  }

  wasAlreadyDismissed() {
    try {
      return sessionStorage.getItem(this.STORAGE_KEY) === 'true';
    } catch (e) {
      // sessionStorage may be unavailable in private browsing
      return false;
    }
  }

  markAsDismissed() {
    try {
      sessionStorage.setItem(this.STORAGE_KEY, 'true');
    } catch (e) {
      // Ignore if sessionStorage is unavailable
    }
  }

  bindEvents() {
    // Close button click
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close());
    }

    // Overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Escape key
    this.handleKeyDown = this.handleKeyDown.bind(this);

    // Offer card clicks - mark as dismissed
    this.popup.querySelectorAll('.promo-popup__offer-card').forEach((card) => {
      card.addEventListener('click', () => {
        this.markAsDismissed();
      });
    });
  }

  handleKeyDown(event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }

    // Tab trap
    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  getFocusableElements() {
    const focusable = this.popup.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    return Array.from(focusable).filter(el => !el.disabled && el.offsetParent !== null);
  }

  trapFocus(event) {
    const focusable = this.getFocusableElements();
    if (focusable.length === 0) return;

    const firstElement = focusable[0];
    const lastElement = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  show() {
    if (!this.popup) return;

    // Store current focus
    this.previousActiveElement = document.activeElement;

    // Show popup
    this.popup.setAttribute('aria-hidden', 'false');
    document.body.classList.add('promo-popup-open');

    // Add keyboard listener
    document.addEventListener('keydown', this.handleKeyDown);

    // Focus the close button for accessibility
    setTimeout(() => {
      if (this.closeButton) {
        this.closeButton.focus();
      }
    }, 100);
  }

  close() {
    if (!this.popup) return;

    // Hide popup
    this.popup.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('promo-popup-open');

    // Remove keyboard listener
    document.removeEventListener('keydown', this.handleKeyDown);

    // Mark as dismissed
    this.markAsDismissed();

    // Restore focus
    if (this.previousActiveElement) {
      this.previousActiveElement.focus();
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new PromoPopup());
} else {
  new PromoPopup();
}
